/**
 * ProphetTimeline — Bilingual FR/AR Sīra timeline of Muhammad ﷺ.
 *
 * Lot 36 — pulls events from `/api/sira/timeline` (80 events imported from
 * the Sealed Nectar / Ar-Raheeq Al-Makhtum). Renders a vertical timeline
 * grouped by `period`, with per-event detail accordion (sections + lessons
 * + sourceRefs). RTL-safe when the active i18n language is Arabic.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  History, MapPin, Filter, Search, ChevronDown, ChevronUp,
  ScrollText, BookOpen,
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  SectionHeader, PageLoading, GlassCard,
} from '../components/shared';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIOD_LABELS = {
  before_birth:           { fr: 'Avant la naissance',    ar: 'قبل الميلاد' },
  birth_and_childhood:    { fr: 'Naissance et enfance',  ar: 'المولد والطفولة' },
  youth:                  { fr: 'Jeunesse',              ar: 'الشباب' },
  before_revelation:      { fr: 'Avant la révélation',   ar: 'قبل الوحي' },
  early_revelation:       { fr: 'Début de la révélation',ar: 'بداية الوحي' },
  meccan_period:          { fr: 'Période mecquoise',     ar: 'العهد المكي' },
  hijrah:                 { fr: 'Hijrah',                ar: 'الهجرة' },
  medinan_period:         { fr: 'Période médinoise',     ar: 'العهد المدني' },
  major_battles:          { fr: 'Grandes batailles',     ar: 'الغزوات الكبرى' },
  treaties_and_delegations: { fr: 'Traités et délégations', ar: 'المعاهدات والوفود' },
  conquest_of_makkah:     { fr: 'Conquête de La Mecque', ar: 'فتح مكة' },
  farewell_pilgrimage:    { fr: 'Pèlerinage d\u2019adieu', ar: 'حجة الوداع' },
  final_days:             { fr: 'Derniers jours',        ar: 'الأيام الأخيرة' },
};

const CONFIDENCE_LABELS = {
  unknown:         { fr: 'Inconnue',           ar: 'غير معلوم' },
  widely_reported: { fr: 'Largement rapporté', ar: 'مشهور' },
  disputed:        { fr: 'Divergent',          ar: 'مختلف فيه' },
  certain:         { fr: 'Établi',             ar: 'ثابت' },
};

const METHOD_NOTE = {
  fr: "Cette timeline est une synthèse pédagogique de la Sīra à partir de sources classiques, notamment Le Nectar Caché. Les événements sont résumés et peuvent être enrichis ou validés progressivement. Certains détails historiques peuvent faire l\u2019objet de divergences entre les sources.",
  ar: "هذه الخط الزمني خلاصة تربوية للسيرة النبوية اعتمادًا على مصادر معروفة، ومن بينها الرحيق المختوم. الأحداث ملخّصة ويمكن مراجعتها وإثراؤها تدريجيًا، وقد توجد بعض التفاصيل التاريخية التي وقع فيها اختلاف بين المصادر.",
};

// Pick FR/AR with graceful fallback when one side is missing.
const pickLang = (obj, lang) => {
  if (obj == null) return null;
  if (typeof obj === 'string') return obj;
  const want = obj[lang];
  if (want && (typeof want !== 'string' || want.trim())) return want;
  if (obj.fr && (typeof obj.fr !== 'string' || obj.fr.trim())) return obj.fr;
  if (obj.ar && (typeof obj.ar !== 'string' || obj.ar.trim())) return obj.ar;
  return null;
};

const periodLabel = (period, lang) =>
  pickLang(PERIOD_LABELS[period], lang) || period;

const formatDate = (date) => {
  if (!date) return '';
  if (typeof date === 'string') return date;
  if (date.gregorianYearText) return date.gregorianYearText;
  if (date.gregorianYear) return String(date.gregorianYear);
  return '';
};

// ---------------------------------------------------------------------------
const TimelineEvent = ({ event, lang, dir, isAdmin }) => {
  const [open, setOpen] = useState(false);
  const title = pickLang(event.title, lang);
  const summary = pickLang(event.summary, lang);
  const detailsList = (event.details && (event.details[lang] || event.details.fr || event.details.ar)) || [];
  const lessons = (event.lessons && (event.lessons[lang] || event.lessons.fr || event.lessons.ar)) || [];
  const dateText = formatDate(event.date);
  const locationText =
    (event.location && (
      lang === 'ar'
        ? (event.location.arName || event.location.name)
        : (event.location.name || event.location.arName)
    )) || null;
  const confLabel = pickLang(CONFIDENCE_LABELS[event.confidence], lang)
    || event.confidence;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4 }}
      className="relative pl-10"
      data-testid={`sira-event-${event.slug}`}
    >
      {/* Node on the spine */}
      <div className="absolute left-2 top-3 w-3 h-3 rounded-full bg-primary border-2 border-background z-10" />
      <GlassCard
        className="hover:border-primary/40 transition-colors"
        dir={dir}
      >
        <div className="flex items-start gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-primary/15 text-primary border-primary/30 border text-[10px]">
                {periodLabel(event.period, lang)}
              </Badge>
              {confLabel && (
                <Badge className="bg-surface-elevated text-text-secondary border border-border text-[10px]">
                  {confLabel}
                </Badge>
              )}
              {isAdmin && event.reviewStatus && (
                <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px]"
                       data-testid={`sira-event-${event.slug}-review`}>
                  {event.reviewStatus}
                </Badge>
              )}
            </div>
            {dateText && (
              <p className="text-[11px] text-text-muted mt-1.5">{dateText}</p>
            )}
            <h3 className={`text-lg font-serif text-text-primary mt-1 ${lang === 'ar' ? 'font-arabic' : ''}`}>
              {title}
            </h3>
            {summary && (
              <p className={`text-sm text-text-secondary mt-2 ${lang === 'ar' ? 'font-arabic leading-relaxed' : ''}`}>
                {summary}
              </p>
            )}
            <div className="flex items-center gap-3 mt-3 text-[11px] text-text-muted flex-wrap">
              {locationText && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {locationText}
                </span>
              )}
              {(event.tags || []).slice(0, 3).map((t) => (
                <span key={t} className="inline-block">#{t}</span>
              ))}
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOpen((o) => !o)}
            data-testid={`sira-event-${event.slug}-toggle`}
            aria-expanded={open}
          >
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <span className="ml-1 text-xs">
              {lang === 'ar' ? 'تفاصيل' : 'Détails'}
            </span>
          </Button>
        </div>

        {open && (
          <div className="mt-3 pt-3 border-t border-border/50 space-y-3"
               data-testid={`sira-event-${event.slug}-details`}>
            {detailsList.map((sec, i) => (
              <div key={i}>
                {sec.heading && (
                  <h4 className={`text-sm font-medium text-text-primary ${lang === 'ar' ? 'font-arabic' : ''}`}>
                    {sec.heading}
                  </h4>
                )}
                {sec.body && (
                  <p className={`text-sm text-text-secondary mt-1 whitespace-pre-line ${lang === 'ar' ? 'font-arabic leading-loose' : ''}`}>
                    {sec.body}
                  </p>
                )}
              </div>
            ))}
            {lessons.length > 0 && (
              <div>
                <h4 className={`text-sm font-medium text-primary ${lang === 'ar' ? 'font-arabic' : ''}`}>
                  <ScrollText className="w-3.5 h-3.5 inline mr-1" />
                  {lang === 'ar' ? 'دروس مستفادة' : 'Enseignements'}
                </h4>
                <ul className={`mt-1 list-disc ${lang === 'ar' ? 'mr-5 font-arabic' : 'ml-5'} text-sm text-text-secondary space-y-1`}>
                  {lessons.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              </div>
            )}
            {event.sourceRefs?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-text-muted hover:text-text-primary"
                         data-testid={`sira-event-${event.slug}-sources`}>
                  <BookOpen className="w-3 h-3 inline mr-1" />
                  {lang === 'ar' ? 'المراجع' : 'Sources'} ({event.sourceRefs.length})
                </summary>
                <ul className={`mt-2 ${lang === 'ar' ? 'mr-4 font-arabic' : 'ml-4'} list-disc text-text-secondary space-y-1`}>
                  {event.sourceRefs.map((s, i) => (
                    <li key={i}>
                      <span className="font-medium">{s.work}</span>
                      {s.author && <span> — {s.author}</span>}
                      {s.location && <span>, {s.location}</span>}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </GlassCard>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
const ProphetTimeline = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('ar') ? 'ar' : 'fr';
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/sira/timeline`);
        setEvents(r.data?.items || []);
      } catch (e) {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    let out = events;
    if (period !== 'all') out = out.filter((e) => e.period === period);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((e) => {
        const t = (pickLang(e.title, lang) || '').toLowerCase();
        const s = (pickLang(e.summary, lang) || '').toLowerCase();
        return t.includes(needle) || s.includes(needle)
          || e.slug.includes(needle);
      });
    }
    return out;
  }, [events, period, q, lang]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of filtered) {
      const k = e.period || 'other';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const periodOptions = useMemo(() => {
    const present = new Set(events.map((e) => e.period));
    return Array.from(present);
  }, [events]);

  if (loading) return <PageLoading />;

  return (
    <div className="min-h-screen" dir={dir} data-testid="sira-timeline-page" data-lang={lang}>
      {/* Hero */}
      <div className="relative py-16 px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background/80 to-background" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span className="overline mb-4 block">
            {lang === 'ar' ? 'السيرة النبوية' : 'Sīra du Prophète Muhammad ﷺ'}
          </span>
          <h1 className="font-arabic text-4xl sm:text-5xl text-primary mb-3" dir="rtl">
            السيرة النبوية
          </h1>
          <h2 className={`text-2xl sm:text-3xl font-serif text-text-primary mb-4 ${lang === 'ar' ? 'font-arabic' : ''}`}>
            {lang === 'ar' ? 'محطات حياة النبي محمد ﷺ' : 'Les grandes étapes de la vie du Prophète ﷺ'}
          </h2>
          <p className={`text-sm text-text-muted max-w-2xl mx-auto ${lang === 'ar' ? 'font-arabic leading-loose' : ''}`}>
            {METHOD_NOTE[lang]}
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="sticky top-16 z-30 bg-background/85 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-text-muted" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[200px] bg-surface border-border" data-testid="sira-period-filter">
              <SelectValue placeholder={lang === 'ar' ? 'الفترة' : 'Période'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {lang === 'ar' ? 'كل الفترات' : 'Toutes les périodes'}
              </SelectItem>
              {periodOptions.map((p) => (
                <SelectItem key={p} value={p}>{periodLabel(p, lang)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <Input
              placeholder={lang === 'ar' ? 'بحث…' : 'Rechercher…'}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 bg-surface"
              data-testid="sira-search"
            />
          </div>
          {(period !== 'all' || q) && (
            <Button size="sm" variant="ghost"
                    onClick={() => { setPeriod('all'); setQ(''); }}
                    data-testid="sira-clear-filters">
              {lang === 'ar' ? 'إظهار الكل' : 'Tout afficher'}
            </Button>
          )}
          <span className="text-xs text-text-muted ml-auto" data-testid="sira-count">
            {filtered.length}{' '}
            {lang === 'ar' ? 'حدثًا' : `événement${filtered.length > 1 ? 's' : ''}`}
          </span>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-4xl mx-auto px-4 py-10">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <History className="w-12 h-12 text-text-muted mx-auto mb-3" />
            <p className="text-text-secondary">
              {lang === 'ar' ? 'لا توجد أحداث.' : 'Aucun événement.'}
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Spine */}
            <div className={`absolute top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary/30 via-primary to-primary/30
              ${dir === 'rtl' ? 'right-2' : 'left-2'}`} />
            <div className="space-y-8">
              {grouped.map(([periodKey, evs]) => (
                <section key={periodKey} className="space-y-3"
                         data-testid={`sira-period-section-${periodKey}`}>
                  <SectionHeader
                    overline={periodLabel(periodKey, lang)}
                    title={`${evs.length} ${lang === 'ar' ? 'أحداث' : 'événements'}`}
                  />
                  {evs.map((event) => (
                    <TimelineEvent
                      key={event.slug}
                      event={event}
                      lang={lang}
                      dir={dir}
                      isAdmin={isAdmin}
                    />
                  ))}
                </section>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProphetTimeline;
